import { ProductCategoryDto } from './ProductCategoryDto';
import { BrandDto } from './BrandDto';
import { ProductImage } from './ProductImage';

/**
 * ProductResponse DTO - represents product/inventory data from backend.
 */
export class ProductResponse {
  constructor(data = {}) {
    this.id = data.id || null;
    this.name = data.name || '';
    this.description = data.description || '';
    this.webDescription = data.webDescription || '';
    this.sku = data.sku || '';
    this.barCode = data.barCode || '';
    this.brand = data.brand instanceof BrandDto
      ? data.brand
      : BrandDto.fromJSON(data.brand);
    this.category = data.category instanceof ProductCategoryDto
      ? data.category
      : ProductCategoryDto.fromJSON(data.category);
    this.listPrice = data.listPrice || 0;
    this.costPrice = data.costPrice || 0;
    this.priceWithVat = data.priceWithVat || 0;
    this.stockQuantity = data.stockQuantity || 0;
    this.minStockLevel = data.minStockLevel || 0;
    this.weightGrams = data.weightGrams ?? null;
    this.daycareTime = data.daycareTime ?? null;
    this.daycareBundle = data.daycareBundle ?? null;
    this.imageThumbUrl = data.imageThumbUrl || null;
    this.imageCardUrl = data.imageCardUrl || null;
    this.imageDetailUrl = data.imageDetailUrl || null;
    this.images = Array.isArray(data.images)
      ? data.images.map((image) => ProductImage.fromJSON(image))
      : [];
    this.tags = Array.isArray(data.tags) ? data.tags : [];
    this.notes = Array.isArray(data.notes) ? data.notes : [];
    this.isFeatured = data.isFeatured !== undefined ? data.isFeatured : false;
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.slug = data.slug || null;
    this.isPubliclyVisible = data.isPubliclyVisible !== undefined ? data.isPubliclyVisible : true;
    // E-commerce switch: null = automatic (grouped on, standalone off).
    this.commerceEnabled = data.commerceEnabled ?? null;
    this.commerceEnabledEffective =
      data.commerceEnabledEffective !== undefined
        ? data.commerceEnabledEffective
        : data.commerceEnabled !== null
          ? data.commerceEnabled
          : Boolean(data.groupMember);
    // Variation-group summary (null when the product is ungrouped).
    this.groupMember = data.groupMember ?? null;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
  }

  /**
   * Creates ProductResponse from JSON object.
   * @param {Object} json - JSON object from API
   * @returns {ProductResponse}
   */
  static fromJSON(json) {
    if (!json) {
      throw new Error('Cannot create ProductResponse from null/undefined');
    }

    return new ProductResponse({
      id: json.id,
      name: json.name,
      description: json.description,
      sku: json.sku,
      barCode: json.barCode,
      brand: BrandDto.fromJSON(json.brand),
      category: ProductCategoryDto.fromJSON(json.category),
      listPrice: json.listPrice,
      costPrice: json.costPrice,
      priceWithVat: json.priceWithVat,
      stockQuantity: json.stockQuantity,
      minStockLevel: json.minStockLevel,
      weightGrams: json.weightGrams,
      daycareTime: json.daycareTime,
      daycareBundle: json.daycareBundle,
      imageThumbUrl: json.imageThumbUrl,
      imageCardUrl: json.imageCardUrl,
      imageDetailUrl: json.imageDetailUrl,
      images: json.images,
      tags: json.tags,
      notes: json.notes,
      isFeatured: json.isFeatured,
      isActive: json.isActive,
      slug: json.slug,
      isPubliclyVisible: json.isPubliclyVisible,
      commerceEnabled: json.commerceEnabled,
      commerceEnabledEffective: json.commerceEnabledEffective,
      groupMember: json.groupMember,
      webDescription: json.webDescription,
      createdAt: json.createdAt,
      updatedAt: json.updatedAt
    });
  }

  /**
   * Validates the product response.
   * @returns {boolean}
   */
  isValid() {
    return !!(this.id && this.name && this.listPrice !== undefined);
  }

  /**
   * Checks if stock is low (below minimum level).
   * @returns {boolean}
   */
  isLowStock() {
    return this.minStockLevel && this.stockQuantity < this.minStockLevel;
  }

  /**
   * Converts to plain object for storage.
   * @returns {Object}
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      webDescription: this.webDescription,
      sku: this.sku,
      barCode: this.barCode,
      brand: this.brand,
      category: this.category?.toJSON?.() || null,
      listPrice: this.listPrice,
      costPrice: this.costPrice,
      priceWithVat: this.priceWithVat,
      stockQuantity: this.stockQuantity,
      minStockLevel: this.minStockLevel,
      weightGrams: this.weightGrams,
      daycareTime: this.daycareTime,
      daycareBundle: this.daycareBundle,
      imageThumbUrl: this.imageThumbUrl,
      imageCardUrl: this.imageCardUrl,
      imageDetailUrl: this.imageDetailUrl,
      images: this.images.map((image) => image.toJSON()),
      isFeatured: this.isFeatured,
      isActive: this.isActive,
      slug: this.slug,
      isPubliclyVisible: this.isPubliclyVisible,
      commerceEnabled: this.commerceEnabled,
      commerceEnabledEffective: this.commerceEnabledEffective,
      groupMember: this.groupMember,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
}
